/**
 * Karte zum Zuordnen: Projekte als Punkte (Farbe = Status), offene Fotos als
 * nummerierte Marken, im aktiven Bündel jedes Foto als kleiner Punkt.
 *
 * Kacheln kommen von OpenStreetMap über unseren Server (`/api/kontor/karte/…`,
 * `kontor_medien.karte_kachel`) — der Browser spricht mit keinem fremden Dienst.
 */
import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { StapelBuendel, ZuordnenProjekt } from "../../hooks/useMedien";

const DRESDEN: L.LatLngTuple = [51.0504, 13.7373];

export function statusFarbe(status: string): string {
  const s = status.toLowerCase();
  if (s.includes("aktiv")) return "#2f7d4f";
  if (s.includes("anfrage")) return "#c08a1e";
  if (s.includes("rechnung")) return "#2d6cb5";
  if (s.includes("verloren")) return "#b5b8b3";
  return "#6b6f6a"; // abgeschlossen und alles andere
}

const esc = (t: string) => t.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

type Props = {
  projekte: ZuordnenProjekt[];
  buendel: StapelBuendel[];
  aktiv: number;
  /** Das fürs aktive Bündel gewählte Projekt — größer dargestellt. */
  gewaehlt: string | null;
  onBuendel: (i: number) => void;
  onProjekt: (p: ZuordnenProjekt) => void;
};

export default function FotoKarte({ projekte, buendel, aktiv, gewaehlt, onBuendel, onProjekt }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const karte = useRef<L.Map | null>(null);
  const projektLage = useRef<L.LayerGroup | null>(null);
  const fotoLage = useRef<L.LayerGroup | null>(null);
  const rufe = useRef({ onBuendel, onProjekt });
  rufe.current = { onBuendel, onProjekt };

  useEffect(() => {
    if (!box.current) return;
    const m = L.map(box.current, { zoomControl: true, scrollWheelZoom: true }).setView(DRESDEN, 12);
    L.tileLayer("/api/kontor/karte/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>-Mitwirkende',
    }).addTo(m);
    m.attributionControl.setPrefix(false);
    projektLage.current = L.layerGroup().addTo(m);
    fotoLage.current = L.layerGroup().addTo(m);
    karte.current = m;
    const beobachter = new ResizeObserver(() => m.invalidateSize());
    beobachter.observe(box.current);
    return () => {
      beobachter.disconnect();
      m.remove();
      karte.current = null;
    };
  }, []);

  useEffect(() => {
    const g = projektLage.current;
    if (!g) return;
    g.clearLayers();
    for (const p of projekte) {
      if (p.breite == null || p.laenge == null) continue;
      const ist = p.projekt_id === gewaehlt;
      const punkt = L.circleMarker([p.breite, p.laenge], {
        radius: ist ? 10 : 7, color: ist ? "#1f2a24" : "#ffffff", weight: ist ? 3 : 2,
        fillColor: statusFarbe(p.status), fillOpacity: 0.95,
      });
      punkt.bindTooltip(`<strong>${esc(p.titel)}</strong>${p.kunde ? `<br>${esc(p.kunde)}` : ""}`, { direction: "top", offset: [0, -8] });
      punkt.on("click", () => rufe.current.onProjekt(p));
      punkt.addTo(g);
      if (ist) punkt.bringToFront();
    }
  }, [projekte, gewaehlt]);

  useEffect(() => {
    const g = fotoLage.current;
    if (!g) return;
    g.clearLayers();
    buendel.forEach((b, i) => {
      if (b.breite == null || b.laenge == null) return;
      const ist = i === aktiv;
      if (ist) {
        for (const f of b.fotos ?? []) {
          if (f.breite == null || f.laenge == null) continue;
          L.circleMarker([f.breite, f.laenge], {
            radius: 3, color: "#b23a2a", weight: 1, fillColor: "#b23a2a", fillOpacity: 0.9, interactive: false,
          }).addTo(g);
        }
      }
      const marke = L.marker([b.breite, b.laenge], {
        icon: L.divIcon({
          className: `fo-karte-marke${ist ? " is-aktiv" : ""}`,
          html: `<span>${b.anzahl}</span>`,
          iconSize: ist ? [36, 36] : [28, 28],
          iconAnchor: ist ? [18, 18] : [14, 14],
        }),
        zIndexOffset: ist ? 1000 : 0,
        title: `${b.anzahl} Fotos`,
      });
      marke.on("click", () => rufe.current.onBuendel(i));
      marke.addTo(g);
    });
  }, [buendel, aktiv]);

  // Ansicht: aufs aktive Bündel (und, wenn gewählt, mit dem Projekt zusammen), sonst auf alle.
  const b = buendel[aktiv];
  const ziel = projekte.find((p) => p.projekt_id === gewaehlt);
  const schluessel = `${b?.ids[0] ?? ""}|${gewaehlt ?? ""}|${buendel.length}`;
  useEffect(() => {
    const m = karte.current;
    if (!m) return;
    if (b && b.breite != null && b.laenge != null) {
      const punkte: L.LatLngTuple[] = [[b.breite, b.laenge]];
      for (const f of b.fotos ?? []) if (f.breite != null && f.laenge != null) punkte.push([f.breite, f.laenge]);
      if (ziel && ziel.breite != null && ziel.laenge != null) punkte.push([ziel.breite, ziel.laenge]);
      m.fitBounds(L.latLngBounds(punkte).pad(0.35), { maxZoom: 17, animate: true });
      return;
    }
    const alle = buendel.filter((x) => x.breite != null && x.laenge != null).map((x) => [x.breite!, x.laenge!] as L.LatLngTuple);
    if (alle.length) m.fitBounds(L.latLngBounds(alle).pad(0.2), { maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);

  return <div ref={box} className="fo-karte-flaeche" role="region" aria-label="Karte mit offenen Fotos und Projekten" />;
}
